// Encryption utilities for backup files using AES-256-GCM

/**
 * Derive encryption key from password using PBKDF2
 * @param {string} password - User password
 * @param {Uint8Array} salt - Salt for key derivation
 * @param {number} iterations - Number of PBKDF2 iterations (default: 100000)
 * @returns {Promise<CryptoKey>} - Derived encryption key
 */
export async function deriveKey(password, salt, iterations = 100000) {
  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);

  // Import password as key material
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );

  // Derive AES-GCM key
  const key = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: iterations,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );

  return key;
}

/**
 * Encrypt backup data with AES-256-GCM
 * @param {string} jsonData - JSON string to encrypt
 * @param {string} password - Encryption password
 * @returns {Promise<Object>} - Encrypted data with metadata
 */
export async function encryptBackup(jsonData, password) {
  if (!password || password.length < 6) {
    throw new Error('Password must be at least 6 characters');
  }

  // Generate random salt and IV
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // Derive encryption key
  const key = await deriveKey(password, salt);

  // Encrypt data
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(jsonData);
  const encryptedBuffer = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv
    },
    key,
    dataBuffer
  );

  // Convert to base64 for storage
  const encryptedArray = new Uint8Array(encryptedBuffer);
  const encryptedBase64 = btoa(String.fromCharCode(...encryptedArray));
  const saltBase64 = btoa(String.fromCharCode(...salt));
  const ivBase64 = btoa(String.fromCharCode(...iv));

  return {
    version: '1.0',
    encrypted: true,
    algorithm: 'AES-256-GCM',
    kdf: 'PBKDF2',
    iterations: 100000,
    salt: saltBase64,
    iv: ivBase64,
    data: encryptedBase64
  };
}

/**
 * Decrypt backup data
 * @param {Object} encryptedData - Encrypted backup object
 * @param {string} password - Decryption password
 * @returns {Promise<string>} - Decrypted JSON string
 */
export async function decryptBackup(encryptedData, password) {
  if (!encryptedData.encrypted) {
    throw new Error('Data is not encrypted');
  }

  if (!password) {
    throw new Error('Password is required for decryption');
  }

  // Decode base64 data
  const salt = Uint8Array.from(atob(encryptedData.salt), c => c.charCodeAt(0));
  const iv = Uint8Array.from(atob(encryptedData.iv), c => c.charCodeAt(0));
  const encryptedBuffer = Uint8Array.from(atob(encryptedData.data), c => c.charCodeAt(0));

  // Derive decryption key
  const iterations = encryptedData.iterations || 100000;
  const key = await deriveKey(password, salt, iterations);

  try {
    // Decrypt data
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      key,
      encryptedBuffer
    );

    // Convert to string
    const decoder = new TextDecoder();
    const jsonData = decoder.decode(decryptedBuffer);

    return jsonData;
  } catch (error) {
    throw new Error('Decryption failed: incorrect password or corrupted data');
  }
}

/**
 * Check if backup data is encrypted
 * @param {Object} data - Backup data object
 * @returns {boolean} - True if encrypted
 */
export function isEncrypted(data) {
  return data && data.encrypted === true;
}
